import { validateResponse, isAppRouteOtherResponse, checkZodSchema, parseJsonQueryObject } from '@ts-rest/core';

class RequestValidationError extends Error {
    constructor(pathParams, headers, query, body) {
        super('[ts-rest] request validation failed');
        this.pathParams = pathParams;
        this.headers = headers;
        this.query = query;
        this.body = body;
    }
}
const validateRequest = (request, reply, schema, options) => {
    const paramsResult = checkZodSchema(request.params, schema.pathParams, {
        passThroughExtraKeys: true,
    });
    const headersResult = checkZodSchema(request.headers, schema.headers, {
        passThroughExtraKeys: true,
    });
    const queryResult = checkZodSchema(options.jsonQuery
        ? parseJsonQueryObject(request.query)
        : request.query, schema.query);
    const bodyResult = checkZodSchema(request.body, 'body' in schema ? schema.body : null);
    if (!paramsResult.success ||
        !headersResult.success ||
        !queryResult.success ||
        !bodyResult.success) {
        throw new RequestValidationError(paramsResult.success ? null : paramsResult.error, headersResult.success ? null : headersResult.error, queryResult.success ? null : queryResult.error, bodyResult.success ? null : bodyResult.error);
    }
    return {
        paramsResult,
        headersResult,
        queryResult,
        bodyResult,
    };
};
const initServer = () => ({
    router: (contract, routes) => ({
        contract,
        routes,
    }),
    registerRouter: (contract, routerImpl, app, options = {
        logInitialization: true,
        jsonQuery: false,
        responseValidation: false,
        requestValidationErrorHandler: 'combined',
    }) => {
        recursivelyRegisterRouter(routerImpl.routes, contract, [], app, options);
        app.setErrorHandler(requestValidationErrorHandler(options.requestValidationErrorHandler));
    },
    plugin: (router) => (app, opts = {
        logInitialization: true,
        jsonQuery: false,
        responseValidation: false,
        requestValidationErrorHandler: 'combined',
    }, done) => {
        recursivelyRegisterRouter(router.routes, router.contract, [], app, opts);
        app.setErrorHandler(requestValidationErrorHandler(opts.requestValidationErrorHandler));
        done();
    },
});
const requestValidationErrorHandler = (handler = 'combined') => {
    return (err, request, reply) => {
        if (err instanceof RequestValidationError) {
            if (handler === 'combined') {
                return reply.status(400).send({
                    pathParameterErrors: err.pathParams,
                    headerErrors: err.headers,
                    queryParameterErrors: err.query,
                    bodyErrors: err.body,
                });
            }
            else {
                return handler(err, request, reply);
            }
        }
        else {
            throw err;
        }
    };
};
const registerRoute = (routeImpl, appRoute, fastify, options) => {
    if (options.logInitialization) {
        console.log(`[ts-rest] Initialized ${appRoute.method} ${appRoute.path}`);
    }
    fastify.route({
        method: appRoute.method,
        url: appRoute.path,
        handler: async (request, reply) => {
            const validationResults = validateRequest(request, reply, appRoute, options);
            const result = await routeImpl({
                params: validationResults.paramsResult.data,
                query: validationResults.queryResult.data,
                headers: validationResults.headersResult.data,
                request,
                body: validationResults.bodyResult.data,
                reply,
            });
            const statusCode = result.status;
            let validatedResponseBody = result.body;
            if (options.responseValidation) {
                const response = validateResponse({
                    appRoute,
                    response: {
                        status: statusCode,
                        body: result.body,
                    },
                });
                validatedResponseBody = response.body;
            }
            const responseType = appRoute.responses[statusCode];
            if (isAppRouteOtherResponse(responseType)) {
                reply.header('content-type', responseType.contentType);
            }
            return reply.status(statusCode).send(validatedResponseBody);
        },
    });
};
const implementationIsInitialisedRouter = (implementation) => {
    return 'contract' in implementation && 'routes' in implementation;
};
const recursivelyRegisterRouter = (routerImpl, appRouter, path, fastify, options) => {
    if (typeof routerImpl === 'object') {
        if (implementationIsInitialisedRouter(routerImpl)) {
            recursivelyRegisterRouter(routerImpl.routes, routerImpl.contract, [...path], fastify, options);
        }
        else {
            for (const key in routerImpl) {
                recursivelyRegisterRouter(routerImpl[key], appRouter[key], [...path, key], fastify, options);
            }
        }
    }
    else if (typeof routerImpl === 'function') {
        registerRoute(routerImpl, appRouter, fastify, options);
    }
};

export { RequestValidationError, initServer };
